"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { categoriesMeta } from "@/lib/categories-meta";
import { buildFilterUrl, removeFilterKey, type FilterParams } from "@/lib/filters";

function FilterIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

const SORT_OPTIONS: { label: string; href: (p: FilterParams, base?: string) => string; active: (p: FilterParams) => boolean }[] = [
  { label: "Önerilen", href: (p, base) => removeFilterKey(p, "sort", base), active: (p) => !p.sort },
  { label: "Fiyat: Düşükten Yükseğe", href: (p, base) => buildFilterUrl(p, { sort: "price-asc" }, base), active: (p) => p.sort === "price-asc" },
  { label: "Fiyat: Yüksekten Düşüğe", href: (p, base) => buildFilterUrl(p, { sort: "price-desc" }, base), active: (p) => p.sort === "price-desc" },
  { label: "İsim (A-Z)", href: (p, base) => buildFilterUrl(p, { sort: "name" }, base), active: (p) => p.sort === "name" },
  { label: "İndirimliler", href: (p, base) => buildFilterUrl(p, { indirim: "1" }, base), active: (p) => p.indirim === "1" },
  { label: "Çok Satanlar", href: (p, base) => buildFilterUrl(p, { badge: "cok-satan" }, base), active: (p) => p.badge === "cok-satan" },
  { label: "Yeni Ürünler", href: (p, base) => buildFilterUrl(p, { badge: "yeni" }, base), active: (p) => p.badge === "yeni" },
];

const PRICE_OPTIONS: { label: string; href: (p: FilterParams, base?: string) => string; active: (p: FilterParams) => boolean }[] = [
  { label: "≤ 200₺", href: (p, base) => buildFilterUrl(p, { min: "", max: "200" }, base), active: (p) => p.max === "200" && !p.min },
  { label: "200 – 350₺", href: (p, base) => buildFilterUrl(p, { min: "200", max: "350" }, base), active: (p) => p.min === "200" && p.max === "350" },
  { label: "350₺+", href: (p, base) => buildFilterUrl(p, { min: "350", max: "" }, base), active: (p) => p.min === "350" && !p.max },
];

export function FilterModal({ params, activeCount, basePath = "/urunler" }: { params: FilterParams; activeCount: number; basePath?: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open ]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-Primary px-4 py-2.5 text-sm font-medium uppercase leading-5 text-White transition hover:bg-Primary-dark active:scale-[0.98] sm:flex-none sm:px-6 lg:hidden"
      >
        <FilterIcon />
        Filtrele
        {activeCount > 0 && (
          <span aria-label={`${activeCount} aktif filtre`} className="flex h-5 min-w-5 items-center justify-center rounded-full bg-White px-1.5 text-xs font-bold text-Primary">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Ürün filtreleri">
          <button
            type="button"
            aria-label="Filtreleri kapat"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/50"
          />
          <div className="absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col overflow-hidden rounded-t-[24px] bg-White shadow-2xl">
            <div className="flex items-center justify-between border-b border-100 px-5 py-4">
              <p className="text-base font-semibold text-Black">
                Filtreler
                {activeCount > 0 && <span className="ml-2 rounded-full bg-Background-light px-2 py-0.5 text-xs font-bold text-Primary">{activeCount} aktif</span>}
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Kapat"
                className="flex h-10 w-10 items-center justify-center rounded-full text-xl text-500 transition hover:bg-Background-light hover:text-Primary"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
              <section aria-label="Kategori">
                <p className="pb-2 text-[11px] font-bold tracking-widest text-500">KATEGORİ</p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    href={buildFilterUrl(params, { clear: true }, basePath)}
                    className={`rounded-full px-4 py-2.5 text-sm transition active:scale-[0.98] ${!params.cat ? "bg-Primary font-medium text-White" : "text-800 outline outline-1 outline-Background-dark hover:bg-Background-light"}`}
                  >
                    Tümü
                  </Link>
                  {categoriesMeta.slice(0, 6).map((c) => (
                    <Link
                      key={c.slug}
                      href={buildFilterUrl(params, { cat: c.slug }, basePath)}
                      className={`rounded-full px-4 py-2.5 text-sm transition active:scale-[0.98] ${params.cat === c.slug ? "bg-Primary font-medium text-White" : "text-800 outline outline-1 outline-Background-dark hover:bg-Background-light"}`}
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </section>

              <section aria-label="Fiyat">
                <p className="pb-2 text-[11px] font-bold tracking-widest text-500">FİYAT</p>
                <div className="flex flex-wrap gap-2">
                  {PRICE_OPTIONS.map((o) => (
                    <Link
                      key={o.label}
                      href={o.href(params, basePath)}
                      className={`rounded-full px-4 py-2.5 text-sm transition active:scale-[0.98] ${o.active(params) ? "bg-Primary font-medium text-White" : "text-800 outline outline-1 outline-Background-dark hover:bg-Background-light"}`}
                    >
                      {o.label}
                    </Link>
                  ))}
                </div>
              </section>

              <section aria-label="Sıralama">
                <p className="pb-2 text-[11px] font-bold tracking-widest text-500">SIRALAMA</p>
                <div className="space-y-1">
                  {SORT_OPTIONS.map((o) => (
                    <Link
                      key={o.label}
                      href={o.href(params, basePath)}
                      className={`flex items-center justify-between rounded-xl px-3 py-3 text-sm transition ${o.active(params) ? "bg-Primary font-medium text-White" : "text-800 hover:bg-Background-light"}`}
                    >
                      {o.label}
                      {o.active(params) && <span aria-hidden="true">✓</span>}
                    </Link>
                  ))}
                </div>
              </section>
            </div>

            <div className="flex items-center gap-2 border-t border-100 bg-White px-5 py-4">
              <Link
                href={basePath}
                className="inline-flex h-12 flex-1 items-center justify-center rounded-full px-6 text-sm font-medium uppercase text-Primary outline outline-1 outline-Background-dark transition hover:bg-Background-light"
              >
                Temizle
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-12 flex-1 items-center justify-center rounded-full bg-Primary px-6 text-sm font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98]"
              >
                Sonuçları Gör
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
